import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPatch, apiPost, apiPut } from "@vimaana/ui";

export type PostAnhang = { nr: number; name: string; mime: string; bytes: number };

/** Eine Mail im Schriftwechsel. Der Text ist ein Fremdtext — anzeigen, nie auswerten. */
export type PostNachricht = {
  id: string;
  richtung: "in" | "out";
  akteur: "kunde" | "lieferant" | "wir" | "";
  absender: string;
  /** "kontakt" oder der Inhaber hinter einer ausgehenden Mail ("croy", "hermsdorf"). */
  absender_schluessel: string;
  zeitpunkt: string;
  betreff: string;
  text: string;
  /** true = die Projektzuordnung ist ein Vorschlag des Imports. */
  vorschlag: boolean;
  postfach: string;
  kopf: { von?: string; an?: string; cc?: string };
  anhaenge: PostAnhang[];
  ausgeblendet: boolean;
};

export type PostProjektwahl = { id: string; titel: string; status: string; erledigt: boolean };

/** Ein Gespraech. Bestaetigen, Umhaengen und Ausblenden gelten fuer das ganze Gespraech. */
export type PostFaden = {
  schluessel: string;
  anker_id: string;
  betreff: string;
  projekt_id: string;
  projekt_titel: string;
  kunde_id: string;
  kunde_name: string;
  vorschlag: boolean;
  /** Warum das Gespraech an diesem Projekt haengt, in Klartext. */
  grund: string;
  ausgeblendet: boolean;
  absender: Record<string, string>;
  anhaenge: number;
  letzte: string;
  anzahl: number;
  nachrichten: PostNachricht[];
  projekte_des_kunden?: PostProjektwahl[];
};

export type PostAbsender = { schluessel: string; name: string };

export type PostAntwort = {
  faeden: PostFaden[];
  nachrichten: number;
  vorschlaege: number;
  ausgeblendet: number;
  absender: PostAbsender[];
  projekte_des_kunden: PostProjektwahl[];
};

export function useProjektPost(projektId?: string) {
  return useQuery<PostAntwort>({
    queryKey: ["post", "projekt", projektId],
    queryFn: () => apiGet<PostAntwort>(`/api/kontor/projekt/${projektId}/post`),
    enabled: !!projektId,
    staleTime: 60_000,
  });
}

export function useKundePost(kundeId?: string) {
  return useQuery<PostAntwort>({
    queryKey: ["post", "kunde", kundeId],
    queryFn: () => apiGet<PostAntwort>(`/api/kontor/kunde/${kundeId}/post`),
    enabled: !!kundeId,
    staleTime: 60_000,
  });
}

export function usePostZuPruefen() {
  return useQuery<{ faeden: PostFaden[] }>({
    queryKey: ["post", "zu-pruefen"],
    queryFn: () => apiGet<{ faeden: PostFaden[] }>("/api/kontor/post/zu-pruefen"),
    staleTime: 60_000,
  });
}

/** „Stimmt" — bestaetigt den Vorschlag fuer das ganze Gespraech. */
export function usePostBestaetigen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) =>
      apiPost<{ ok: boolean; nachrichten: number }>(`/api/kontor/post/${messageId}/bestaetigen`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["post"] }),
  });
}

/** Haengt das ganze Gespraech um. `projektId` leer = bleibt am Kunden, ohne Projekt. */
export function usePostUmhaengen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ messageId, projektId }: { messageId: string; projektId: string }) =>
      apiPost<{ ok: boolean; nachrichten: number }>(`/api/kontor/post/${messageId}/umhaengen`, {
        projekt_id: projektId || null,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["post"] }),
  });
}

/**
 * Ab einem Zeitpunkt gehoert der Verlauf zu einem anderen Projekt (Migration 176).
 * `messageIds` = je betroffenem Gespraech die erste Nachricht ab dem Punkt. Der Server
 * schneidet die Mailverlaeufe; ist der Abschnitt davor schon im Zielprojekt, wachsen
 * beide wieder zusammen (so laesst sich ein Schnitt aufheben).
 */
export function usePostAbHier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ messageIds, projektId }: { messageIds: string[]; projektId: string }) =>
      apiPost<{ ok: boolean; nachrichten: number; gespraeche: number; geteilt: number; zusammengefuehrt: number }>(
        `/api/kontor/post/ab-hier`, { projekt_id: projektId || null, message_ids: messageIds }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["post"] });
      qc.invalidateQueries({ queryKey: ["nachrichten"] });
      qc.invalidateQueries({ queryKey: ["projekt"] });
    },
  });
}

/** Blendet aus oder holt zurueck — das ganze Gespraech oder eine einzelne Nachricht. Geloescht wird nichts. */
export function usePostAusblenden() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ messageId, gespraech, ausblenden }: { messageId: string; gespraech: boolean; ausblenden: boolean }) =>
      apiPost<{ ok: boolean; nachrichten: number }>(`/api/kontor/post/${messageId}/ausblenden`, {
        gespraech,
        ausblenden,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["post"] }),
  });
}

export function anhangUrl(messageId: string, nr: number): string {
  return `/api/kontor/post/${messageId}/anhang/${nr}`;
}

/** Der Zeitraum, nach dem Post dem Projekt zugeordnet wird. Leer von Hand = automatisch. */
export type ProjektZeitraum = {
  von: string; bis: string; von_auto: string; bis_auto: string;
  von_von_hand: boolean; bis_von_hand: boolean;
};
export type ZeitraumWirkung = ProjektZeitraum & { geholt: number; dazu: number; weg: number; hinweis: string };

export function useProjektZeitraum(projektId?: string) {
  return useQuery<ProjektZeitraum>({
    queryKey: ["post", "zeitraum", projektId],
    queryFn: () => apiGet<ProjektZeitraum>(`/api/kontor/projekt/${projektId}/zeitraum`),
    enabled: !!projektId,
    staleTime: 60_000,
  });
}

/** Setzt die Grenzen; der Server holt Post nach und ordnet Unbestaetigtes neu zu. */
export function useProjektZeitraumSetzen(projektId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { von: string; bis: string }) =>
      apiPut<ZeitraumWirkung>(`/api/kontor/projekt/${projektId}/zeitraum`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["post"] }),
  });
}

/** Ein Leed: ein Gmail-Gespraech mit dem Label „Leed", bevor daraus ein Kunde wird. */
export type Leed = {
  id: string; betreff: string; status: "offen" | "projekt" | "verworfen"; postfach: string;
  name: string; email: string; telefon: string; strasse: string; plz: string; ort: string;
  kundentyp: "privat" | "firma"; leadquelle: string; anliegen: string; notiz: string;
  kunde_id: string; kunde_name: string; projekt_id: string; projekt_titel: string;
  erste_mail_am: string; letzte_mail_am: string; bearbeitet_von: string;
  faeden: PostFaden[]; mails: number;
  /** Stammdaten des bekannten Kunden — zum Vergleich mit dem, was am Leed steht. */
  kunde_stamm: { name: string; email: string; telefon: string; strasse: string; plz: string; ort: string; kundentyp: string } | null;
};
export type LeedsAntwort = { leeds: Leed[]; zahlen: Record<string, number>; leadquellen: string[] };
export type LeedFelder = Partial<Pick<Leed,
  "name" | "email" | "telefon" | "strasse" | "plz" | "ort" | "kundentyp" | "leadquelle" | "anliegen" | "notiz">>
  & { status?: "offen" | "verworfen" };

export function useLeeds(status: "offen" | "projekt" | "verworfen") {
  return useQuery<LeedsAntwort>({
    queryKey: ["leeds", status],
    queryFn: () => apiGet<LeedsAntwort>(`/api/kontor/leeds?status=${status}`),
    staleTime: 60_000,
  });
}

export function useLeedPatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, felder }: { id: string; felder: LeedFelder }) =>
      apiPatch<{ ok: boolean }>(`/api/kontor/leed/${id}`, felder),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["leeds"] }),
  });
}

/**
 * Ein Kunde, der der Absender eines Leeds sein könnte. `gruende` nennt, was passt
 * („Mailadresse", „ähnliche Mailadresse", „Telefon", „Name", „Anschrift");
 * `hart` = gleiche Mailadresse, dann legt der Server keinen neuen Kunden an.
 */
export type LeedKandidat = {
  id: string; name: string; anschrift: string; email: string; anrede: string;
  gruende: string[]; hart: boolean;
  /** true = die Mailadresse des Leeds steht beim Kunden schon (Mailfeld oder Kontakte). */
  mail_bekannt: boolean;
};

export function useLeedKandidaten(leedId: string | undefined) {
  return useQuery({
    queryKey: ["leeds", "kandidaten", leedId],
    queryFn: () => apiGet<{ kandidaten: LeedKandidat[] }>(`/api/kontor/leed/${leedId}/kandidaten`).then((r) => r.kandidaten),
    enabled: !!leedId,
    staleTime: 60_000,
  });
}

export type LeedUmwandelnBody = {
  kunde_id: string; projekt_titel: string; projekt_ort: string; projektart: string;
  /** Nur mit kunde_id: abweichende Mail des Leeds ersetzt die alte oder kommt dazu. */
  mail?: "" | "ersetzen" | "dazu";
  /** Ohne kunde_id: trotz ähnlicher Kunden neu anlegen (gleiche Mail sperrt weiterhin). */
  trotzdem_neu?: boolean;
};

export function useLeedUmwandeln() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: LeedUmwandelnBody }) =>
      apiPost<{
        ok: boolean; kunde_id: string; projekt_id: string; mails: number;
        /** true = neuer Kunde angelegt, false = einem bestehenden zugeordnet. */
        neu: boolean;
        /** Was mit der Mailadresse des Leeds geschah: "ersetzt", "dazu" oder "". */
        mail: "" | "ersetzt" | "dazu";
      }>(`/api/kontor/leed/${id}/umwandeln`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["leeds"] });
      qc.invalidateQueries({ queryKey: ["post"] });
      qc.invalidateQueries({ queryKey: ["kunden"] });
      qc.invalidateQueries({ queryKey: ["projekte"] });
    },
  });
}

/** Gerade gelabelte Gespraeche sofort holen, statt auf den naechsten Postlauf zu warten. */
export function useLeedsHolen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<{ neue_leeds: number; neue_mails: number }>(`/api/kontor/leeds/holen`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["leeds"] }),
  });
}

/** Aus Kontor heraus schreiben — aus dem eigenen Postfach, die Mail steht sofort im Verlauf. */
export type SchreibenBody = {
  an: string; betreff: string; text: string;
  projekt_id?: string; kunde_id?: string; anfrage_id?: string; lieferant_firma_id?: string;
  antwort_auf?: string | null; kopie_an_mich?: boolean; cc?: string;
  /** Anfrage an einen Lieferanten: diese Projekt-Materialien werden „angefragt" (Migration 175). */
  material_anfrage?: string[];
  /** Nachfrage zu dieser Anfrage — die Wiedervorlage beginnt neu. */
  anfrage_nachfassen?: string;
  /** Claras Entwurf, aus dem der Text entstand — die KI-Spur hält Entwurf und gesendeten Text nebeneinander fest. */
  entwurf_id?: string;
};
export type SchreibenErgebnis = { gesendet: boolean; an: string[]; anfrage_id?: string | null; hinweis?: string };
export function usePostSchreiben() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SchreibenBody) =>
      apiPost<SchreibenErgebnis>(`/api/kontor/post/schreiben`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projekt-material"] });
      qc.invalidateQueries({ queryKey: ["post"] });
      qc.invalidateQueries({ queryKey: ["leeds"] });
      qc.invalidateQueries({ queryKey: ["projekt"] });
    },
  });
}

/** Clara schlaegt einen Text vor. Dauert bis zu einer Minute; ohne Evo gibt es keinen. */
export function usePostEntwurf() {
  return useMutation({
    mutationFn: (body: { projekt_id?: string; anfrage_id?: string; kunde_id?: string; lieferant_firma_id?: string; an?: string; stichworte?: string; betreff?: string }) =>
      apiPost<{ betreff: string; text: string; auftrag_id?: string }>(`/api/kontor/post/entwurf`, body),
  });
}

/** Wem man aus einem Projekt heraus schreiben kann: Kunde, Ansprechpartner, Lieferanten mit Belegen am Projekt. */
export type ProjektAdressen = { ziele: { email: string; wer: string; art: "kunde" | "lieferant" }[]; kunde_id: string };
export function useProjektAdressen(projektId?: string, aktiv = true) {
  return useQuery<ProjektAdressen>({
    queryKey: ["post", "adressen", projektId],
    queryFn: () => apiGet<ProjektAdressen>(`/api/kontor/projekt/${projektId}/adressen`),
    enabled: !!projektId && aktiv,
    staleTime: 5 * 60_000,
  });
}

/** Adressen, die in den Gespraechen des Projekts mitschreiben, aber nicht beim Kunden stehen. */
export type NeueAdressen = { adressen: { email: string; name: string; seit: string; betreff: string }[]; kunde_id: string; kunde_name: string };
export function useNeueAdressen(projektId?: string) {
  return useQuery<NeueAdressen>({
    queryKey: ["post", "neue-adressen", projektId],
    queryFn: () => apiGet<NeueAdressen>(`/api/kontor/projekt/${projektId}/neue-adressen`),
    enabled: !!projektId,
    staleTime: 60_000,
  });
}
export function useNeueAdresse(projektId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { email: string; aktion: "zweitadresse" | "ansprechpartner" | "ignorieren"; name?: string; rolle?: string }) =>
      apiPost<{ ok: boolean }>(`/api/kontor/projekt/${projektId}/neue-adresse`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["post", "neue-adressen", projektId] });
      qc.invalidateQueries({ queryKey: ["post", "adressen", projektId] });
      qc.invalidateQueries({ queryKey: ["kunden"] });
    },
  });
}

/** Die eigene Signatur und die Postfaecher der anderen Inhaber (fuer „Kopie an"). */
export type MailSignatur = { signatur: string; email: string; partner: { email: string; name: string }[] };
export function useMailSignatur() {
  return useQuery<MailSignatur>({
    queryKey: ["mail", "signatur"],
    queryFn: () => apiGet<MailSignatur>(`/api/kontor/mail/signatur`),
    staleTime: 5 * 60_000,
  });
}
/** Ein Postfach mit Signatur — alle Inhaber, zum Pflegen in den Einstellungen. */
export type MailPostfach = { owner_id: string; name: string; email: string; signatur: string; eigenes: boolean };
export function useMailSignaturen() {
  return useQuery<{ postfaecher: MailPostfach[] }>({
    queryKey: ["mail", "signaturen"],
    queryFn: () => apiGet<{ postfaecher: MailPostfach[] }>(`/api/kontor/mail/signaturen`),
    staleTime: 5 * 60_000,
  });
}
/** Speichert eine Signatur — ohne `owner_id` die eigene, sonst die des anderen Inhabers. */
export function useMailSignaturSpeichern() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { signatur: string; owner_id?: string }) =>
      apiPut<{ signatur: string }>(`/api/kontor/mail/signatur`, v),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mail", "signatur"] });
      qc.invalidateQueries({ queryKey: ["mail", "signaturen"] });
    },
  });
}

/** Was am Leed korrigiert wurde, in den Kundenstamm übernehmen (ohne Mailadresse). */
export function useLeedStammdaten() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiPost<{ ok: boolean; geaendert: string[] }>(`/api/kontor/leed/${id}/stammdaten`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["leeds"] });
      qc.invalidateQueries({ queryKey: ["kunden"] });
    },
  });
}
